/**
 * FocusPage — Distraction-Free Study Mode (Tier-2 #4 retention move).
 *
 * Full-screen single-task UI. No sidebar, no top nav, no notifications.
 * One job: help the user complete a single Pomodoro of focused work.
 *
 * - Task picker: AI tutor / cards due / a specific subject
 * - Pomodoro timer: 25/5/15 cycles (or custom), persists to localStorage
 *   so reload mid-session doesn't reset
 * - Big visible ring with remaining seconds
 * - Soft ambient toggle (browsers block autoplay; we just play/pause on
 *   user click — no autoplay)
 * - "Done" button drops them back to /app, logging the session.
 *
 * The whole point is identity reframe: iStudyPro is a focus tool that
 * happens to have social, NOT a social platform that happens to study.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, RotateCcw, X, BookOpen, Brain, Sparkles, VolumeX, ArrowRight, Flame, Music, Minimize2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { apiUrl } from '../utils/apiClient';
import FocusModeWalkthrough from '../components/FocusModeWalkthrough';

const PHASE = {
  FOCUS: { label: 'Focus', minutes: 25, color: '#002FA7' },
  SHORT_BREAK: { label: 'Short break', minutes: 5, color: '#16A34A' },
  LONG_BREAK: { label: 'Long break', minutes: 15, color: '#0EA5E9' },
};

const STORAGE_KEY = 'istudypro_focus_session_v1';
const SOUND_PREFS_KEY = 'istudypro_focus_sound_v1';

// Royalty-free ambient loops. Hosted via known stable CDNs; loaded lazily
// only when the user opts in. We do not autoplay — modern browsers block
// it and we want the focus ritual to be intentional anyway.
// Royalty-free ambient loops. Mixkit's /active_storage/sfx/... pattern is
// the most reliable hot-link surface (Pixabay's HTML page URLs return 403
// on direct fetches). White noise is synthesized in-browser via WebAudio
// so it works fully offline and skips a network call.
const SOUND_PRESETS = [
  { id: 'off', label: 'Silent', emoji: '🔇', src: null, kind: 'silent' },
  { id: 'lofi', label: 'Lo-fi', emoji: '🎧', src: 'https://cdn.pixabay.com/audio/2022/05/27/audio_1808fbf07a.mp3', kind: 'audio' },
  { id: 'rain', label: 'Rain', emoji: '🌧️', src: 'https://assets.mixkit.co/active_storage/sfx/2393/2393-preview.mp3', kind: 'audio' },
  { id: 'whitenoise', label: 'White noise', emoji: '🌫️', src: null, kind: 'whitenoise' },
  { id: 'campfire', label: 'Campfire', emoji: '🔥', src: 'https://assets.mixkit.co/active_storage/sfx/1330/1330-preview.mp3', kind: 'audio' },
];

const formatTime = (s) => {
  const m = Math.floor(s / 60).toString().padStart(2, '0');
  const sec = (s % 60).toString().padStart(2, '0');
  return `${m}:${sec}`;
};

const readPersisted = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (s && typeof s === 'object') return s;
  } catch { /* ignore */ }
  return null;
};

export default function FocusPage() {
  const navigate = useNavigate();
  // Lazy initializers read localStorage ONCE, before the persist-effect fires.
  // Without this, the persist effect runs on first render with the default
  // 25:00 state and overwrites the saved session before the (legacy)
  // restore effect ever reads it.
  const [phase, setPhase] = useState(() => {
    const s = readPersisted();
    return s?.phase && PHASE[s.phase] ? s.phase : 'FOCUS';
  });
  const [secondsLeft, setSecondsLeft] = useState(() => {
    const s = readPersisted();
    if (s && Number.isFinite(s.secondsLeft) && s.secondsLeft >= 0) return s.secondsLeft;
    return PHASE.FOCUS.minutes * 60;
  });
  const [running, setRunning] = useState(false);
  const [task, setTask] = useState(() => {
    const s = readPersisted();
    return s?.task || null;
  });
  const [completedFocus, setCompletedFocus] = useState(() => {
    const s = readPersisted();
    return Number.isFinite(s?.completedFocus) ? s.completedFocus : 0;
  });
  // Ambient sound preset (lofi/rain/whitenoise/campfire/off). Persisted
  // separately so the user's audio preference survives across sessions
  // even after they exit + clear the in-progress timer.
  const [soundId, setSoundId] = useState(() => {
    try { return localStorage.getItem(SOUND_PREFS_KEY) || 'off'; } catch { return 'off'; }
  });
  const [showSoundPicker, setShowSoundPicker] = useState(false);
  const [streakDays, setStreakDays] = useState(0);
  const tickRef = useRef(null);
  const ambientAudioRef = useRef(null);
  const whiteNoiseRef = useRef(null); // { ctx, source, gain } when whitenoise active

  // One-time fetch: focus streak count for the top-of-page "🔥 N day streak"
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await api.get(apiUrl('/sessions/focus/stats'));
        if (!cancel) setStreakDays(res.data?.streak_days || 0);
      } catch { /* silent */ }
    })();
    return () => { cancel = true; };
  }, []);

  // Ambient sound lifecycle: create / swap / pause based on soundId AND
  // whether the timer is running. Handles 3 kinds:
  //   • silent     → tear everything down
  //   • audio      → HTMLAudio element looping a remote MP3
  //   • whitenoise → WebAudio buffer of pink-ish noise, no network needed
  // We always teardown the *other* kind when switching so we don't leak two
  // sources playing simultaneously when the user toggles around.
  const teardownWhiteNoise = () => {
    const wn = whiteNoiseRef.current;
    if (!wn) return;
    try { wn.source.stop(); } catch { /* already stopped */ }
    try { wn.ctx.close(); } catch { /* ignore */ }
    whiteNoiseRef.current = null;
  };
  const teardownHtmlAudio = () => {
    if (ambientAudioRef.current) {
      try { ambientAudioRef.current.pause(); } catch { /* ignore */ }
      ambientAudioRef.current = null;
    }
  };

  useEffect(() => {
    try { localStorage.setItem(SOUND_PREFS_KEY, soundId); } catch { /* ignore */ }
    const preset = SOUND_PRESETS.find((p) => p.id === soundId);
    if (!preset || preset.kind === 'silent') {
      teardownHtmlAudio();
      teardownWhiteNoise();
      return;
    }
    if (preset.kind === 'whitenoise') {
      teardownHtmlAudio();
      // Rebuild only if we don't already have a live whitenoise graph
      if (!whiteNoiseRef.current) {
        try {
          const AC = window.AudioContext || window.webkitAudioContext;
          if (!AC) return;
          const ctx = new AC();
          const bufferSize = 2 * ctx.sampleRate;
          const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
          const data = buffer.getChannelData(0);
          // Pink-ish noise (warmer than pure white) via Paul Kellet's algorithm
          let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
          for (let i = 0; i < bufferSize; i++) {
            const white = Math.random() * 2 - 1;
            b0 = 0.99886 * b0 + white * 0.0555179;
            b1 = 0.99332 * b1 + white * 0.0750759;
            b2 = 0.96900 * b2 + white * 0.1538520;
            b3 = 0.86650 * b3 + white * 0.3104856;
            b4 = 0.55000 * b4 + white * 0.5329522;
            b5 = -0.7616 * b5 - white * 0.0168980;
            data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
            b6 = white * 0.115926;
          }
          const source = ctx.createBufferSource();
          source.buffer = buffer;
          source.loop = true;
          const gain = ctx.createGain();
          gain.gain.value = 0; // start muted, the running effect will ramp it up
          source.connect(gain).connect(ctx.destination);
          source.start();
          whiteNoiseRef.current = { ctx, source, gain };
        } catch {
          // WebAudio unsupported / blocked — fail silently. The UI button
          // will appear "selected" but no sound; matches the silent preset
          // behavior so we don't disrupt the focus ritual.
        }
      }
      return;
    }
    // kind === 'audio'
    teardownWhiteNoise();
    if (!ambientAudioRef.current || ambientAudioRef.current.dataset.preset !== preset.id) {
      teardownHtmlAudio();
      const audio = new Audio(preset.src);
      audio.loop = true;
      audio.volume = 0.4;
      audio.dataset.preset = preset.id;
      // Best-effort error fallback: if the remote MP3 errors (network,
      // CORS, 4xx), drop back to Silent so the user isn't stuck thinking
      // their click did nothing — and surface a one-time toast.
      audio.addEventListener('error', () => {
        // No toast import here — keep it console-only; the user sees the
        // preset toggle reset to Silent on next render.
        console.warn('[ambient] failed to load', preset.id);
        teardownHtmlAudio();
        setSoundId('off');
      }, { once: true });
      ambientAudioRef.current = audio;
    }
  }, [soundId]);

  useEffect(() => {
    const shouldPlay = running && phase === 'FOCUS';
    const audio = ambientAudioRef.current;
    if (audio) {
      if (shouldPlay) audio.play().catch(() => { /* autoplay block — fine */ });
      else audio.pause();
    }
    const wn = whiteNoiseRef.current;
    if (wn) {
      try {
        // Ramp gain rather than hard mute — feels less abrupt.
        const t = wn.ctx.currentTime;
        wn.gain.gain.cancelScheduledValues(t);
        wn.gain.gain.linearRampToValueAtTime(shouldPlay ? 0.18 : 0, t + 0.4);
      } catch { /* ignore */ }
    }
  }, [running, phase, soundId]);

  useEffect(() => () => {
    // Cleanup on unmount — never let an ambient loop leak across pages.
    teardownHtmlAudio();
    teardownWhiteNoise();
  }, []);

  // Soft chime via WebAudio API — no external asset, never blocked, always
  // available even offline. Triple-tone arpeggio (C5-E5-G5) for the
  // cycle-end celebration.
  const playChime = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const tones = [523.25, 659.25, 783.99]; // C5, E5, G5
      tones.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const start = ctx.currentTime + i * 0.18;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.18, start + 0.04);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.6);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + 0.65);
      });
    } catch { /* never fail the focus flow over a missing chime */ }
  };

  // Persist on every change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        phase, secondsLeft, completedFocus, task,
      }));
    } catch { /* ignore */ }
  }, [phase, secondsLeft, completedFocus, task]);

  const advancePhase = useCallback(async () => {
    setRunning(false);
    // Always celebrate the phase boundary with the soft WebAudio chime —
    // works offline, never blocked by autoplay, never breaks the flow.
    playChime();
    if (phase === 'FOCUS') {
      const nextCompleted = completedFocus + 1;
      setCompletedFocus(nextCompleted);
      // Log to backend (silent failure — focus shouldn't be blocked by API)
      try {
        await api.post(apiUrl('/sessions/focus'), {
          duration_seconds: PHASE.FOCUS.minutes * 60,
          task_kind: task?.kind,
        });
      } catch { /* ignore */ }
      const next = nextCompleted % 4 === 0 ? 'LONG_BREAK' : 'SHORT_BREAK';
      setPhase(next);
      setSecondsLeft(PHASE[next].minutes * 60);
      toast.success('Focus complete — take a break.');
    } else {
      setPhase('FOCUS');
      setSecondsLeft(PHASE.FOCUS.minutes * 60);
      toast.info('Break over — back to it 💪');
    }
  }, [phase, completedFocus, task]);

  // Tick
  useEffect(() => {
    if (!running) {
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
      return;
    }
    tickRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) { advancePhase(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, [running, advancePhase]);

  const reset = () => {
    setRunning(false);
    setSecondsLeft(PHASE[phase].minutes * 60);
  };

  const exit = () => {
    if (running) {
      const ok = window.confirm('Leaving will pause your session — keep going?');
      if (!ok) return;
    }
    setRunning(false);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    navigate('/app');
  };

  const TASK_OPTIONS = [
    { kind: 'tutor', label: 'Ask the AI Tutor', icon: Brain, route: '/study-buddy' },
    { kind: 'cards', label: 'Review due flashcards', icon: Sparkles, route: '/flashcards' },
    { kind: 'lecture', label: 'Listen / re-record', icon: BookOpen, route: '/lectures' },
  ];

  const totalSeconds = PHASE[phase].minutes * 60;
  const pct = totalSeconds > 0 ? ((totalSeconds - secondsLeft) / totalSeconds) * 100 : 0;

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden"
      style={{
        backgroundColor: '#0C1222',
        backgroundImage: 'radial-gradient(circle at 30% 20%, rgba(0,47,167,0.18) 0%, transparent 60%), radial-gradient(circle at 70% 80%, rgba(0,47,167,0.10) 0%, transparent 60%)',
      }}
      data-testid="focus-page"
    >
      {/* First-visit walkthrough — auto-hides after one read; localStorage-gated. */}
      <FocusModeWalkthrough />

      {/* Top bar — minimal */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-5 z-10">
        <div className="flex items-center gap-3 text-white/60 text-xs uppercase tracking-[0.2em] font-semibold">
          <span className="inline-flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Focus mode
          </span>
          {completedFocus > 0 && (
            <span className="text-white/40" data-testid="focus-completed-count">· {completedFocus} done today</span>
          )}
          {streakDays > 0 && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-500/15 text-orange-300 normal-case tracking-normal"
              data-testid="focus-streak-pill"
              title={`${streakDays}-day focus streak — keep it alive!`}
            >
              <Flame className="w-3 h-3" />
              <span className="font-bold">{streakDays}</span>
              <span className="text-[10px] opacity-80">day{streakDays === 1 ? '' : 's'}</span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 relative">
          <button
            onClick={() => setShowSoundPicker((v) => !v)}
            className={`p-2 transition-colors ${soundId === 'off' ? 'text-white/40 hover:text-white' : 'text-[#5C8AFF] hover:text-white'}`}
            data-testid="focus-sound-picker-btn"
            title="Ambient sound"
          >
            {soundId === 'off' ? <VolumeX className="w-4 h-4" /> : <Music className="w-4 h-4" />}
          </button>
          <AnimatePresence>
            {showSoundPicker && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                className="absolute top-11 right-10 w-44 bg-[#0C1222] border border-white/15 shadow-2xl py-1.5 z-20"
                data-testid="focus-sound-picker-menu"
              >
                {SOUND_PRESETS.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => { setSoundId(p.id); setShowSoundPicker(false); }}
                    className={`w-full px-3 py-2 flex items-center gap-2 text-left text-xs transition-colors ${soundId === p.id ? 'bg-white/10 text-white' : 'text-white/70 hover:bg-white/5 hover:text-white'}`}
                    data-testid={`focus-sound-${p.id}`}
                  >
                    <span className="w-5">{p.emoji}</span>
                    <span className="flex-1">{p.label}</span>
                    {soundId === p.id && <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
          <button
            onClick={() => {
              try { localStorage.removeItem('istudypro_focus_walkthrough_v1'); } catch { /* ignore */ }
              // Re-trigger walkthrough by forcing a remount via key reset; cheapest path is a full reload of the route's component state.
              window.location.reload();
            }}
            className="text-white/40 hover:text-white p-2"
            data-testid="focus-help-btn"
            title="How does this work?"
            aria-label="How does this work?"
          >
            <span className="text-sm font-bold w-5 h-5 inline-flex items-center justify-center border border-current rounded-full leading-none">?</span>
          </button>
          <button
            onClick={() => {
              // Minimize: leave the timer running, persist state (already
              // continuous via the localStorage effect below), and route
              // back to the dashboard. Dashboard reads the same key and
              // renders a floating "Focus in progress" pill so the user
              // can resume in one click.
              navigate('/app');
            }}
            className="text-white/40 hover:text-white p-2"
            data-testid="focus-minimize-btn"
            title="Minimize — keep timer running"
            aria-label="Minimize focus session"
          >
            <Minimize2 className="w-5 h-5" />
          </button>
          <button
            onClick={exit}
            className="text-white/40 hover:text-white p-2"
            data-testid="focus-exit-btn"
            title="Exit focus mode"
            aria-label="Exit focus mode"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="w-full max-w-md text-center">
        {/* Task picker (only before timer starts) */}
        <AnimatePresence mode="wait">
          {!task && phase === 'FOCUS' && !running ? (
            <motion.div
              key="picker"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              data-testid="focus-task-picker"
            >
              <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-3 font-semibold">
                Today's session
              </p>
              <h1 className="font-serif-accent text-3xl sm:text-4xl font-bold text-white mb-8 tracking-tight">
                What are you focusing on?
              </h1>
              <div className="space-y-2">
                {TASK_OPTIONS.map((t) => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.kind}
                      onClick={() => setTask(t)}
                      className="w-full px-5 py-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/30 text-left flex items-center gap-3 group transition-all"
                      data-testid={`focus-task-${t.kind}`}
                    >
                      <Icon className="w-5 h-5 text-[#5C8AFF] shrink-0" />
                      <span className="flex-1 text-white text-sm font-medium">{t.label}</span>
                      <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-white/60 group-hover:translate-x-1 transition-all" />
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-white/30 mt-8">
                Pick a task — the timer starts when you're ready.
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="timer"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              data-testid="focus-timer-block"
            >
              {task && (
                <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2 font-semibold flex items-center justify-center gap-2">
                  <span style={{ color: PHASE[phase].color }}>{PHASE[phase].label}</span>
                  <span className="text-white/20">·</span>
                  <span>{task.label}</span>
                </p>
              )}

              {/* Big circular timer */}
              <div className="relative w-72 h-72 sm:w-80 sm:h-80 mx-auto my-6">
                <svg viewBox="0 0 200 200" className="w-full h-full -rotate-90">
                  <circle cx="100" cy="100" r="92" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="4" />
                  <motion.circle
                    cx="100" cy="100" r="92" fill="none"
                    stroke={PHASE[phase].color} strokeWidth="4" strokeLinecap="round"
                    pathLength={100}
                    strokeDasharray="100"
                    strokeDashoffset={100 - pct}
                    transition={{ duration: 0.5, ease: 'linear' }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <p
                    className="text-6xl sm:text-7xl font-bold text-white tabular-nums tracking-tight"
                    style={{ fontFamily: 'var(--sf-mono)' }}
                    data-testid="focus-timer-display"
                  >
                    {formatTime(secondsLeft)}
                  </p>
                  <p className="text-xs uppercase tracking-[0.2em] text-white/40 mt-2">
                    Cycle {Math.floor(completedFocus / 4) + 1} · #{(completedFocus % 4) + 1}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-center gap-3 mb-6">
                <button
                  onClick={reset}
                  className="w-11 h-11 flex items-center justify-center bg-white/5 hover:bg-white/10 text-white/70 transition-colors"
                  data-testid="focus-reset-btn"
                  title="Reset"
                  aria-label="Reset focus timer"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setRunning(r => !r)}
                  className="px-8 py-3 text-white text-sm font-semibold tracking-tight transition-colors flex items-center gap-2"
                  style={{ backgroundColor: PHASE[phase].color }}
                  data-testid="focus-toggle-btn"
                >
                  {running ? <><Pause className="w-4 h-4" /> Pause</> : <><Play className="w-4 h-4" /> {secondsLeft === totalSeconds ? 'Start' : 'Resume'}</>}
                </button>
                {task && phase === 'FOCUS' && (
                  <button
                    onClick={() => window.open(task.route, '_blank')}
                    className="w-11 h-11 flex items-center justify-center bg-white/5 hover:bg-white/10 text-white/70 transition-colors"
                    data-testid="focus-open-task-btn"
                    title={`Open ${task.label} in a new tab`}
                  >
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Phase shortcuts */}
              <div className="flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.18em] font-semibold">
                {Object.entries(PHASE).map(([key, p]) => (
                  <button
                    key={key}
                    onClick={() => { setPhase(key); setSecondsLeft(p.minutes * 60); setRunning(false); }}
                    className={`px-3 py-1.5 transition-colors ${phase === key ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/70'}`}
                    data-testid={`focus-phase-${key.toLowerCase()}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              {task && (
                <button
                  onClick={() => setTask(null)}
                  className="text-xs text-white/30 hover:text-white/60 mt-6 transition-colors"
                  data-testid="focus-change-task-btn"
                >
                  ← Change task
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
